<script lang="ts">
	import type { Offer, OfferDesignatedEntity } from './types/offer'
	import { type RequestDto, RequestType } from './types/requestDto'
	import Card from '../atoms/Card.svelte'
	import FormattedHeaderValue, { inputTypes } from '../atoms/FormattedHeaderValue.svelte'
	import { t } from '$i18n/i18n'
	import { formatNumber } from '../utils/formatters.js'
	import { getContext, hasContext } from 'svelte'
	import { writable, type Writable } from 'svelte/store'
	import TextArea from '../atoms/TextArea.svelte'
	import { isEmptyOffer } from './offerUtils.js'
	import type { Lot } from '../types/lot'

	interface Props {
		offer: Offer
		requestType: RequestType
	}

	// we don't need to set it, if it can be shown depending on the type, but if we DONT want to show it (e.g. Saas) we can set it to false
	const showKickback: Writable<boolean> = hasContext('showKickback')
		? getContext('showKickback')
		: writable(false)
	const request: Writable<RequestDto> = getContext('request')
	const lot: Writable<Lot> = getContext('lot')
	const offerDesignatedEntity: Writable<OfferDesignatedEntity> = getContext('offerDesignatedEntity')

	let { offer, requestType }: Props = $props()

	let isOutdated: boolean = $derived(
		(offer?.facts?.volume ?? 0) > ($request?.facts?.volume ?? $lot.volume ?? 0)
	)

	/**
	 * offer.facts.kickback from the backend may hold only the request's fixed minimum, so the
	 * shown kickback must be recomputed against the actual offer price. derived, not written
	 * back to offer.facts: Facts is a plain class instance, so mutating it is invisible to
	 * Svelte reactivity — OfferPriceCard recomputes and persists the kickback on edit
	 */
	let effectiveKickback: number | null = $derived.by(() => {
		const fixedKickback = offer?.facts?.kickback || $request?.facts?.kickback || 0
		const kickbackPercent =
			offer?.facts?.kickbackInPercent || $request?.facts?.kickbackInPercent || 0
		if (offer?.facts?.price && (fixedKickback || kickbackPercent)) {
			// only one of kickback/kickbackPercent can be given
			const kickbackOfActualPrice = Math.round(offer.facts.price * kickbackPercent) / 100
			return Math.max(fixedKickback, kickbackOfActualPrice)
		}
		return offer?.facts?.kickback ?? null
	})
	let priceExclKickback: number = $derived(
		Math.max(0, Math.round(((offer?.facts?.price ?? 0) - (effectiveKickback ?? 0)) * 100) / 100)
	)
	let { offerPlace, headerText } = $derived.by(() => {
		/**
		 * $offerDesignatedEntity is shared across all three offer types (material,
		 * materialWithTransport, transport) and gets overwritten by whichever one last reloaded, so
		 * it can hold a different offer's label. offer.offerCandidateRequestedEntity.id/label are
		 * scoped to this specific offer and are the correct primary source; the context is only used
		 * as a fallback, and only when its id still matches this offer's own id
		 */
		const offerOwnLabel =
			offer?.offerCandidateRequestedEntity?.id && offer?.offerCandidateRequestedEntity?.label
				? offer.offerCandidateRequestedEntity.label
				: ''
		const contextLabel =
			offer?.offerCandidateRequestedEntity?.id &&
			$offerDesignatedEntity?.id === offer.offerCandidateRequestedEntity?.id &&
			$offerDesignatedEntity?.label
				? $offerDesignatedEntity.label
				: ''
		const designatedEntityLabel = offerOwnLabel || contextLabel
		const designatedEntityString = designatedEntityLabel
			? designatedEntityLabel + (offer?.offerLocation ? ': ' : '')
			: ''
		const addressString = offer?.offerLocation || ''
		return {
			headerText: designatedEntityString
				? $t('UI.counterOffer.acceptPop.label')
				: $t('UI.counterOffer.acceptPlace.label'),
			offerPlace: designatedEntityString + addressString
		}
	})
</script>

<div class="{requestType.toLowerCase()} offer-facts-info__data">
	<div class="offer-facts-info__facts">
		<Card borderRadius="0.625rem" boxShadow="" height="100%" padding="1.25rem">
			<div class="offer-info-card-data">
				<FormattedHeaderValue
					{isOutdated}
					title={$t('UI.lot.volume.label')}
					value={offer?.facts?.volume || '-'}
					unit={$t('UI.unit.ton.short.small')}
					type={inputTypes.TONNAGE}
					tooltipMessage={$t('UI.tooltip.optimizer.outdated.quote')}
				/>
				<FormattedHeaderValue
					title={$t('Offer.dailyVolume')}
					value={offer?.facts?.dailyCapacity || '-'}
					unit={$t('UI.unit.ton.short.small')}
					type={inputTypes.TONNAGE}
				/>
				<FormattedHeaderValue
					title={$t('UI.counterOffer.vehicleCategory.label')}
					value={!isEmptyOffer(offer, requestType) ? offer?.facts?.vehiclesAsString : '-'}
				/>
				<div class="offer__dates">
					<FormattedHeaderValue
						title={$t('UI.startDate.label')}
						value={offer?.facts?.startAsFormattedDate || '-'}
					/>
					<FormattedHeaderValue
						title={$t('UI.endDate.label')}
						value={offer?.facts?.endAsFormattedDate || '-'}
					/>
				</div>
			</div>
		</Card>
	</div>
	<div class="offer-facts-info__price">
		<Card borderRadius="0.625rem" boxShadow="" height="100%" padding="1.25rem">
			<div class="offer-info-card-data" class:hideKickback={!$showKickback}>
				{#if $showKickback}
					<FormattedHeaderValue
						title={$t('UI.offer.priceExclKickback')}
						value={priceExclKickback || priceExclKickback === 0
							? formatNumber(priceExclKickback.toFixed(2))
							: '-'}
						unit={$t('UI.unit.eurPerTon.short.small')}
					/>
					<FormattedHeaderValue
						title={$t('Model.kickback')}
						value={effectiveKickback || effectiveKickback === 0
							? formatNumber(effectiveKickback.toFixed(2))
							: '-'}
						unit={$t('UI.unit.eurPerTon.short.small')}
					/>
					<div class="offer-price-container_separator">
						<hr />
					</div>
				{/if}
				<FormattedHeaderValue
					title={$showKickback
						? $t('UI.offer.priceInclKickback')
						: $t('UI.lot.grossPricePerTon.Get.label')}
					value={offer?.facts.priceValFormattedAsString}
					unit={$t('UI.unit.eurPerTon.short.small')}
				/>
			</div>
		</Card>
	</div>
	{#if requestType === RequestType.MATERIAL}
		<div class="offer-facts-info__place">
			<Card borderRadius="0.625rem" boxShadow="" padding="1.25rem">
				<div class="offer-info-card-data">
					<FormattedHeaderValue title={headerText} value={offerPlace} />
				</div>
			</Card>
		</div>
	{/if}
	<div class="offer-facts-info__comment" class:transport={requestType === RequestType.TRANSPORT}>
		<Card
			borderRadius="0.625rem"
			boxShadow=""
			padding="1.25rem"
			height={requestType === RequestType.TRANSPORT ? '100%' : 'initial'}
		>
			<div class="offer-info-card-data">
				<TextArea
					id={requestType === RequestType.MATERIAL
						? 'furtherDetailsMaterial'
						: 'furtherDetailsMaterialWithTransport'}
					text={offer.furtherDetailsComment}
					title={$t('UI.Model.moreDetails')}
					placeholder={$t('UI.remarks.noRemarks')}
					marginTop="0"
					readonly
				/>
			</div>
		</Card>
	</div>
</div>

<style lang="scss">
	@use '$styles/global';

	.offer-info-card-data {
		@include global.flex-col(1.25rem, $alignment: flex-start, $justify: space-between);

		height: 100%;

		&.hideKickback {
			align-items: center;
			justify-content: center;

			:global(.container-header-value) {
				align-items: center;
				justify-content: center;
			}

			:global(.container-header-value span) {
				font-size: 1rem !important;
			}
		}

		.offer__dates {
			@include global.flex-row(1.5rem, $justify: flex-start, $alignment: center);

			width: 100%;
		}
	}

	.offer-facts-info__data {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-template-rows: subgrid;
		gap: 1.375rem 2rem;
		grid-row: 2/5; /* rows 2-5 of the parent grid */

		/* we only have header and card rows here */
		&.transport {
			grid-template-columns: repeat(4, 1fr);
			grid-row: 2/3;
		}
	}

	.offer-price-container_separator {
		width: 100%;
		text-align: right;

		hr {
			color: global.$primaryGreen;
			height: 1px;
			width: 100%;
			margin: 1rem 0 0.7rem;
		}
	}

	/* order of the rows for desktop, not important for mobile since we have a one-row layout there */
	@media screen and (min-width: global.$medium) {
		.material {
			grid-column: 2/2;
		}

		.material_with_transport {
			grid-column: 1/1;
		}

		/* transport uses all columns */
		.transport {
			grid-column: 1/-1;
		}

		.offer-facts-info__price {
			grid-row: 1 / 2;

			/* for transport, we have a 4 col subgrid */
			&.transport {
				grid-column: 2/2;
			}
		}

		.offer-facts-info__facts {
			grid-row: 1 / 2;

			/* for transport, we have a 4 col subgrid */
			&.transport {
				grid-column: 1/1;
			}
		}

		.offer-facts-info__place {
			grid-column: 1 / 3;
		}

		.offer-facts-info__comment {
			grid-column: 1 / 3;

			/* for transport on desktop we have a subgrid of 4 columns, so it should take the complete right side */
			&.transport {
				grid-column: 3/-1;

				/* to make the textarea span the complete height */
				& :global(.textarea-container),
				& :global(.input-field-error-message-container),
				& :global(textarea) {
					height: 100% !important;
				}
			}
		}
	}

	.offer-facts-info__place {
		grid-column: 1 / 3;
	}

	.offer-facts-info__comment {
		grid-column: 1 / 3;
		grid-row: 3/3;

		&.transport {
			grid-row: 1/3;
		}
	}

	.offer-facts-info__place {
		grid-row: 2/2;
	}

	/* Tablets */
	@media screen and (max-width: global.$medium) {
		/* for tablet size screens, we do not show offers beneath each other, but below each other */
		.material,
		.transport,
		.material_with_transport {
			grid-column: 1/1;
		}

		.material {
			grid-row: 6/9;
		}

		// container of the fact cards
		.offer-facts-info__container {
			grid-template-columns: 1fr;
		}

		.offer-facts-info__data {
			grid-template-columns: 1fr 1fr;

			/* for one column layout, we want the comments below, so we need two rows of the subgrid */
			&.transport {
				grid-row: 2/4;
				grid-template-columns: repeat(2, 1fr);
			}
		}

		.transport.offer-facts-info__comment {
			grid-row: 2/2;
			grid-column: 1/-1;
		}
	}

	/* Phones */
	@media screen and (max-width: global.$xsmall) {
		// container of the fact cards
		.offer-facts-info__data {
			grid-template-columns: 1fr;
		}

		.material.offer-facts-info__data {
			grid-row: 6/10;

			& .offer-facts-info__facts {
				grid-row: 1/1;
			}

			& .offer-facts-info__price {
				grid-row: 2/2;
			}

			& .offer-facts-info__place {
				grid-row: 3/3;
				grid-column: 1/1;
			}

			& .offer-facts-info__comment {
				grid-row: 4/4;
				grid-column: 1/1;
			}
		}
	}
</style>
